import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, useParams } from 'react-router-dom';
import { cityByRoute } from '@/features/worlds/city/cities';
import { AppShell } from '@/components/layout/AppShell';
import { Placeholder } from '@/components/common/Scaffold';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';

// Route-level lazy: 첫 진입은 shell+현재 뷰만 싣고, 무거운 편집/3D/그래프 표면은 필요할 때 로드한다.
const OverviewView = lazy(() => import('@/features/overview/OverviewView').then((m) => ({ default: m.OverviewView })));
const CodexView = lazy(() => import('@/features/codex/CodexView').then((m) => ({ default: m.CodexView })));
const WorldsView = lazy(() => import('@/features/worlds/WorldsView').then((m) => ({ default: m.WorldsView })));
const CharactersView = lazy(() => import('@/features/characters/CharactersView').then((m) => ({ default: m.CharactersView })));
const PlotView = lazy(() => import('@/features/plot/PlotView').then((m) => ({ default: m.PlotView })));
const GraphView = lazy(() => import('@/features/graph/GraphView').then((m) => ({ default: m.GraphView })));
const AtlasView = lazy(() => import('@/features/worlds/atlas/AtlasView').then((m) => ({ default: m.AtlasView })));
const ManuscriptView = lazy(() => import('@/features/manuscript/ManuscriptView').then((m) => ({ default: m.ManuscriptView })));
const ReadingRoomView = lazy(() => import('@/features/readingroom/ReadingRoomView').then((m) => ({ default: m.ReadingRoomView })));
const LibraryView = lazy(() => import('@/features/library/LibraryView').then((m) => ({ default: m.LibraryView })));
const ConceptsView = lazy(() => import('@/features/concepts/ConceptsView').then((m) => ({ default: m.ConceptsView })));
const QuestionsView = lazy(() => import('@/features/questions/QuestionsView').then((m) => ({ default: m.QuestionsView })));
const KernelHome = lazy(() => import('@/features/console/KernelHome').then((m) => ({ default: m.KernelHome })));
const SettingsView = lazy(() => import('@/features/settings/SettingsView').then((m) => ({ default: m.SettingsView })));

/** 구 도시 라우트 → 아틀라스 노드 redirect(북마크·e2e 호환). cityId→locId 매핑. */
function CityRedirect() {
  const { planetId = '', cityId = '' } = useParams();
  const spec = cityByRoute(planetId, cityId);
  return <Navigate to={spec ? `/atlas/${spec.locId}` : '/worlds'} replace />;
}

function Lazy({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary
      fallback={
        <div className="p-16 text-center font-mono text-xs uppercase tracking-[0.2em] text-faint">
          뷰를 불러오지 못했습니다 — 새로고침 해주세요.
        </div>
      }
    >
      <Suspense
        fallback={
          <div className="p-16 text-center font-mono text-xs uppercase tracking-[0.2em] text-faint">로딩…</div>
        }
      >
        {children}
      </Suspense>
    </ErrorBoundary>
  );
}

/**
 * 라우트 = 모듈 뷰(02 §11). M0는 전부 Placeholder로 연결되고,
 * 각 마일스톤이 해당 element를 실제 뷰로 교체한다.
 * 엔티티 딥링크는 path param 대신 `?focus=<id>` 인스펙터 방식(02 §11).
 */
export const router = createBrowserRouter([
  {
    path: '/',
    element: <AppShell />,
    children: [
      {
        // F0(에이전트-퍼스트): 앱의 전면 = 커널. 화면들은 좌측 '직접 편집' 수동 영역으로.
        index: true,
        element: (
          <Lazy>
            <KernelHome />
          </Lazy>
        ),
      },
      {
        path: 'overview',
        element: (
          <Lazy>
            <OverviewView />
          </Lazy>
        ),
      },
      { path: 'console', element: <Navigate to="/" replace /> },
      {
        path: 'codex',
        element: (
          <Lazy>
            <CodexView />
          </Lazy>
        ),
      },
      {
        path: 'worlds',
        element: (
          <Lazy>
            <WorldsView />
          </Lazy>
        ),
      },
      { path: 'worlds/:planetId/:cityId', element: <CityRedirect /> },
      {
        path: 'atlas/:nodeId',
        element: (
          <Lazy>
            <AtlasView />
          </Lazy>
        ),
      },
      {
        path: 'characters',
        element: (
          <Lazy>
            <CharactersView />
          </Lazy>
        ),
      },
      {
        path: 'plot',
        element: (
          <Lazy>
            <PlotView />
          </Lazy>
        ),
      },
      {
        path: 'graph',
        element: (
          <Lazy>
            <GraphView />
          </Lazy>
        ),
      },
      {
        path: 'manuscript',
        element: (
          <Lazy>
            <ManuscriptView />
          </Lazy>
        ),
      },
      {
        path: 'reading',
        element: (
          <Lazy>
            <ReadingRoomView />
          </Lazy>
        ),
      },
      {
        path: 'library',
        element: (
          <Lazy>
            <LibraryView />
          </Lazy>
        ),
      },
      {
        path: 'concepts',
        element: (
          <Lazy>
            <ConceptsView />
          </Lazy>
        ),
      },
      {
        path: 'questions',
        element: (
          <Lazy>
            <QuestionsView />
          </Lazy>
        ),
      },
      {
        path: 'settings',
        element: (
          <Lazy>
            <SettingsView />
          </Lazy>
        ),
      },
      { path: '*', element: <Placeholder title="없는 경로" desc="이 좌표엔 별이 없다." milestone="—" /> },
    ],
  },
]);
